import { Button, Heading } from '@cue/atoms';
import { useCrispChat } from '@cue/hooks';
import { LanguageSelector } from '@cue/molecules';
import { Modal, useModal } from '@cue/organisms';
import { AnalyticsTransformer, LocalStorageUtil, Mdx } from '@cue/utility';
import { useResendAccess } from '@project/hooks/use-resend-access';
import { useUser } from '@project/hooks/use-user';
import { availableLanguages } from '@project/i18n/config';
import { useFlag } from '@project/providers/flags-provider';
import { Consent } from '@project/site/core/consent/consent';
import { HeaderMenu, HeaderMenuRef } from '@project/site/layout/header';
import { SiemensChatBot } from '@project/specific/components/siemens-chat-bot';
import { OnSiteNotification } from '@project/specific/page-wrapper/on-site';
import { useQuery } from '@tanstack/react-query';
import { navigate } from 'gatsby';
import { useEffect, useRef, useState } from 'react';
import { isMobile } from 'react-device-detect';
import { useTranslation } from 'react-i18next';
import { Logo } from '../logo';
import { OnSiteToggle, OnSiteToggleHandle } from './onsite-toggle';

export const Header: React.FC<HeaderProps> = ({ lang, onSiteToggle = false, className = '' }) => {
  const { i18n, t } = useTranslation();
  const headerMenuRef = useRef<HeaderMenuRef>(null);
  const { user } = useUser();
  const onSiteToggleFlag = useFlag('onsite-toggle');
  const [chatBlockedByCookieSettingsModalRef] = useModal();
  const [homeLink, setHomeLink] = useState<string>('');
  const { chat, load, setUser } = useCrispChat({
    needCookieConsent: true,
    CRISP_WEBSITE_ID: process.env.CRISP_WEBSITE_ID || '',
    options: { autoload: false },
  });

  const onSiteToggleRef = useRef<OnSiteToggleHandle>(null);
  const onSiteToggleMobileRef = useRef<OnSiteToggleHandle>(null);
  const { data: onsiteMode } = useQuery({
    queryKey: ['onsite-mode'],
    queryFn: () => (LocalStorageUtil.get('onSiteMode') as boolean) || false,
  });
  useEffect(() => {
    chat.onChatClosed(() => {
      chat.hide();
      document.body.setAttribute('data-crisp-open', 'false');

      AnalyticsTransformer.trackEvent({
        category: 'support',
        type: 'chat',
        action: 'close',
      });
    });

    chat.onChatOpened(() => {
      document.body.setAttribute('data-crisp-open', 'true');
    }); // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  useEffect(() => {
    if (user) {
      setUser(`${user.firstName} ${user.lastName} (${user.userId})`);
    } else {
      setUser('');
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  const changeLanguage = (lang: string) => {
    let target = ""
    if (typeof window !== 'undefined') {
      // feedback has other structure
      if (document.location.pathname.match(/feedback\/[a-z]{2}/)) {
        //navigate(`/feedback/${lang}/${document.location.search}`);
        target = (`/feedback/${lang}/${document.location.search}`);
      } else {
        /* navigate(
          `${document.location.pathname.replace(/\/(.{1,2})\/?/, `/${lang}/`)}${
            document.location.search
          }`,
        ); */
        target = 
          `${document.location.pathname.replace(/\/(.{1,2})\/?/, `/${lang}/`)}${
            document.location.search
          }`
        

          window.document.location.href = target

      }
    }
  };
  useEffect(() => {
    setHomeLink(onsiteMode ? '/onsite-mode' : '/');
  }, [onsiteMode]);

  const { setIsOpen: setResendAccessModalOpen } = useResendAccess();

  const customAction = async (action: string) => {
    let cookieConsent;
    switch (action) {
      case 'open_resend_modal':
        setResendAccessModalOpen({ isAdhoc: false });
        if (headerMenuRef?.current?.closeMobile) headerMenuRef.current.closeMobile();
        break;
      case 'open-support-chat':
        cookieConsent = await Consent.areAllConsentsAccepted();
        if (cookieConsent) {
          load();
          chat.show();
          chat.open();
          AnalyticsTransformer.trackEvent({
            category: 'support',
            type: 'chat',
            action: 'open',
          });
          return;
        }

        chatBlockedByCookieSettingsModalRef?.current?.open();
        break;
      default:
        break;
    }
  };

  return (
    <>
      {onSiteToggleFlag && (
        <>
          <OnSiteNotification
            onDimensionChange={(height) => {
              //setNotificationHeight(height);
              document.documentElement.style.setProperty('--notification-height', `${height}px`);
            }}
            onSelect={(selectedTag) => {
              onSiteToggleRef.current?.update();
              onSiteToggleMobileRef.current?.update();

              if (selectedTag?.value === 'true') {
                document.location.href = `/${i18n.language}/onsite-mode`;
              }
            }}
          />
        </>
      )}
      <header
        className={`cue-project-header ${className} ${
          isMobile ? 'cue-project-header-mobile' : ''
        }`}>
        <div className="cue-project-header-inner">
          <Logo uri={`/${i18n.language}${homeLink}`} />

          <nav className="cue-project-menu cue-project-menu-main">
            <HeaderMenu slug="header-menu-left" onCustomAction={customAction} />
          </nav>
          <nav className="cue-project-menu cue-project-menu-right">
            {/* <HeaderMenu slug="header-menu-right-top" onCustomAction={customAction} /> */}
            {(onSiteToggle || onSiteToggleFlag) && (
              <OnSiteToggle ref={onSiteToggleRef} lang={lang} />
            )}
          </nav>
          <nav className="cue-project-menu cue-project-menu-right-mobile">
            <div className="cue-project-menu-mobile-onsite-toggle">
              {(onSiteToggle || onSiteToggleFlag) && (
                <OnSiteToggle ref={onSiteToggleMobileRef} lang={lang} />
              )}
            </div>
            <HeaderMenu
              onCustomAction={customAction}
              ref={headerMenuRef}
              slug="header-menu-left"
              isMobile
              additionalIcon="arrowhead-right"
              mobileNav={{
                id: 'cue-mobile-slide-in',
                customCloseRenderer: ({ onClose }) => (
                  <Button
                    icon="close"
                    styling="modalClosePlain"
                    onClick={() => {
                      AnalyticsTransformer.trackEvent({
                        category: 'general',
                        type: 'mobileMenu',
                        action: 'close',
                      });
                      onClose && onClose();
                    }}
                  />
                ),
              }}
              additionalRenderer={() => (
                <>
                  <HeaderMenu
                    slug="header-menu-right"
                    direction="vertical"
                    onCustomAction={customAction}
                  />
                  <LanguageSelector
                    languages={availableLanguages as string[]}
                    styling="menuItem"
                    render={({ value }) => value.toUpperCase()}
                    action={({ value }) => {
                      changeLanguage(value);
                    }}
                  />

                  <SiemensChatBot showOn="mobile" />
                </>
              )}
            />
          </nav>
          <nav className="cue-project-menu cue-project-menu-right-secondary">
            <HeaderMenu slug="header-menu-right" onCustomAction={customAction} />
          </nav>
          {availableLanguages.length > 1 && (
            <div className="cue-project-menu cue-project-menu-language">
              <LanguageSelector
                languages={availableLanguages}
                styling="menuItem"
                render={({ value }) => value.toUpperCase()}
                action={({ value }) => {
                  changeLanguage(value);
                }}
              />
            </div>
          )}
        </div>
      </header>
      <Modal ref={chatBlockedByCookieSettingsModalRef}>
        <div className="crisp-chat-blocked-modal">
          <Heading type="h3">{t('crispchat.blocked.headline')}</Heading>
          <Mdx>{t('crispchat.blocked.message')}</Mdx>
        </div>
      </Modal>
    </>
  );
};

export interface HeaderProps {
  lang: string;
  path: string;
  styling?: string;
  className?: string;
  isFrontPage?: boolean;
  onSiteToggle?: boolean;
}
